import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Play, Table2 } from 'lucide-react'
import { fetchApi, OrzForgeApiError } from '../lib/api'
import type { DbQueryResponse, DbTablesResponse } from '../types'

export function DbPage() {
  const [sql, setSql] = useState('SELECT name FROM sqlite_master WHERE type = \'table\'')
  const [result, setResult] = useState<DbQueryResponse | null>(null)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const tablesQuery = useQuery<DbTablesResponse>({
    queryKey: ['db-tables'],
    queryFn: async () => (await fetchApi('/v1/db/tables')) as DbTablesResponse,
    staleTime: 60_000,
  })

  async function runQuery() {
    setRunning(true)
    setError(null)
    setResult(null)
    try {
      const data = (await fetchApi('/v1/db/query', {
        method: 'POST',
        body: JSON.stringify({ sql }),
      })) as DbQueryResponse
      setResult(data)
    } catch (err) {
      setError(err instanceof OrzForgeApiError ? err.message : 'Error ejecutando query')
    } finally {
      setRunning(false)
    }
  }

  const columns = result?.results.length
    ? Object.keys(result.results[0] as Record<string, unknown>)
    : []

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">OrzDB</h1>
          <p className="text-sm text-gray-400">
            {tablesQuery.data?.tables.length ?? 0} tablas encontradas
          </p>
        </div>
        <div className="rounded-lg border border-white/10 bg-panel px-3 py-1.5 text-xs text-gray-400">
          Solo consultas SELECT / PRAGMA
        </div>
      </header>

      <div className="rounded-xl border border-white/10 bg-panel p-5">
        <div className="mb-3 flex items-center gap-2 text-sm text-gray-400">
          <Table2 className="h-4 w-4" />
          Tablas
        </div>
        {tablesQuery.isLoading ? (
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
        ) : (
          <div className="flex flex-wrap gap-2">
            {tablesQuery.data?.tables.map((t, i) => (
              <span
                key={i}
                className="rounded-md bg-white/5 px-2.5 py-1 text-sm font-mono text-brand-300"
              >
                {(t as { name?: string }).name ?? JSON.stringify(t)}
              </span>
            ))}
            {!tablesQuery.data?.tables.length && (
              <span className="text-sm text-gray-500">No hay tablas visibles.</span>
            )}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-white/10 bg-panel p-5">
        <label htmlFor="sql" className="mb-2 block text-sm font-medium text-gray-300">
          Query SQL
        </label>
        <textarea
          id="sql"
          value={sql}
          onChange={(e) => setSql(e.target.value)}
          rows={6}
          className="w-full rounded-lg border border-white/10 bg-surface p-3 font-mono text-sm text-white outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
        />
        <div className="mt-3 flex items-center gap-3">
          <button
            onClick={runQuery}
            disabled={running}
            className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-500 disabled:opacity-60"
          >
            <Play className="h-4 w-4" />
            {running ? 'Ejecutando...' : 'Ejecutar'}
          </button>
        </div>
        {error && (
          <div className="mt-3 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            {error}
          </div>
        )}
      </div>

      {result && (
        <div className="overflow-hidden rounded-xl border border-white/10 bg-panel">
          <div className="max-h-[32rem] overflow-auto">
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 bg-panel">
                <tr className="border-b border-white/10">
                  {columns.map((col) => (
                    <th key={col} className="px-4 py-3 font-semibold text-gray-300">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.results.map((row, idx) => (
                  <tr key={idx} className="border-b border-white/5 hover:bg-white/[0.02]">
                    {columns.map((col) => (
                      <td key={col} className="px-4 py-2.5 font-mono text-gray-400">
                        {row[col] === null
                          ? 'NULL'
                          : typeof row[col] === 'object'
                            ? JSON.stringify(row[col])
                            : String(row[col])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {!result.results.length && (
              <div className="p-6 text-center text-sm text-gray-500">Sin resultados</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
